import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as ordersService from '../../services/api/orders';
import { Order } from '../../entities/order';
import OrdersTableComponent from '../../components/orders/table';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import OrdersFilter from '../../components/orders/filter';
import OrdersFilterProps from '../../entities/ordersFilter';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import { strings } from '../../localization/strings';
import { cleanParamsForUrl } from '../../services/formatter/createQueryStringObject';

function OrdersListPageComponent() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const page = query.get('page');

  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();

  const initialState: OrdersFilterProps = useMemo(() => {
    const state: OrdersFilterProps = {};

    searchParams.forEach((value, key) => {
      if (key === 'page') {
        state.page = parseInt(value, 10);
      } else if (key === 'startDate' || key === 'endDate') {
        state[key] = new Date(value);
      } else if (key === 'importStatus' || key === 'orderStatus' || key === 'textSearch' || key === 'siteId') {
        state[key] = value;
      }
    });

    return state;
  }, [searchParams]);

  const [params, setParams] = useState<OrdersFilterProps>(initialState);
  const [pages, setPages] = useState<number>(1);
  const [currPage, setCurrPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const items = await ordersService.list(token, params);

        setOrders(items.orders);
        setPages(items.pages);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, params, currPage]);

  const setFilterData = (filterData: OrdersFilterProps) => {
    setParams({ ...filterData, page: currPage });
    setSearchParams(cleanParamsForUrl(filterData));
  };

  const setPageNo = (currentPage: number) => {
    setCurrPage(currentPage);
    setParams({ ...params, page: currentPage });
    setSearchParams(cleanParamsForUrl({ ...params, page: currentPage }));
  };

  useEffect(() => {
    if (page) {
      setCurrPage(Number(page));
    }
  }, [page]);

  return (
    <div>
      <header className="mb-3 mt-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">Orders</h1>
        </div>
      </header>
      <main>
        <div className="w-full px-3">
          {error && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-5">
              <AlertPrimary text={error} />
            </div>
          )}
          <OrdersFilter onUpdate={setFilterData} filterState={params} />
          {isLoading ? (
            <div className="flex justify-center py-12">
              <CommonAnimatedSpinner />
            </div>
          ) : (
            <>
              {orders.length > 0 && (
                <OrdersTableComponent orders={orders} pages={pages} onUpdate={setPageNo} currPageNo={currPage} />
              )}
              {orders.length === 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{strings.search.noResults}</div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default OrdersListPageComponent;
